/**
 * The attention list's live readings: the pure half of `useAttentionModel`
 * (twin slice 1, plan W5 steps 11 and 14).
 *
 * The selector (`state/attention/select.ts`) is pure and takes its inputs
 * whole. This file turns the console's live data into those inputs, and
 * owns the one piece of state the selector cannot: the debounce.
 *
 * WHAT A READING IS. The debounce raises a condition only across two
 * readings 10 s apart, so a reading must be NEW DATA, never a re-render:
 * `AttentionBook.read` folds a source once per identity -- the data it was
 * read from, by reference -- and a render that hands over the same data
 * folds nothing. A robots reading is the robot records and the registry's
 * issue list (and whether either could be read, and the feed clamp).
 * Presence changing on a timer is NOT a robots reading: a robot's
 * conditions come from its record and the registry, and re-reading the
 * same record when its mark fades would count one old record twice. A
 * robot going DARK on the clock (lost or checking) is a GLANCE (A17b,
 * `robotUnreadKey`): it reads nothing, so it is never a second reading of
 * the same record, but it dates the hold of what the robot had at the
 * flip, so its card freezes as it was when it went dark (A11) and not at
 * whatever poll comes next.
 *
 * EACH CAR IS A READING OF ITS OWN (A19). What the cars said is read one
 * car at a time (`liftCarSource`): a car's reading is its own row and what
 * W6's column state says of it (`liftCarReadingKey`), and nothing about
 * another car is a reading of it. So another car turning lost, the site
 * row turning "Can't tell" on a car's silence, or another car's new row
 * never reads again a car whose row did not change -- one row saying
 * "kept out" stays one reading. W6's site row is a source of its own too
 * (`LIFT_SITE_SOURCE`): read when the row changes, or a car it speaks for
 * sends a new row. A car turning quiet is the clock, not the car saying
 * anything again (A17a), so it is not a reading of it. A car turning LOST
 * is a reading of that car alone: a lost car shows only its silence (a
 * `lift-silence` key), so the reading shows nothing it said and, as a
 * glance would, only dates the hold of the card it had (A7).
 *
 * THE SILENCE IS READ ON THE CLOCK. A car that stops reporting is a
 * condition of its own, "No reading", and so is the site row it makes
 * ("Can't tell whether robots can change floors"). W6 decides both on the
 * device clock, and once a car is lost its column state stops changing:
 * no new data will ever come to make a second reading of the silence. So
 * those keys (`isLiftSilenceKey`) are a source of their own,
 * `lift-silence`, which a later look at the same column state also reads:
 * the hook folds it again when a key of it that one reading has shown
 * could raise (`AttentionBook.dueAtMs`). Every other lift key is what a
 * car SAID, which only a new row speaks for.
 *
 * SILENCE IS NOT EVIDENCE. A source that failed, is still loading, or is
 * read through a clamped feed is folded as a reading that shows nothing
 * and speaks for nothing (A7, `cannotRead` true for every key of it): it
 * neither raises nor clears nor counts toward either. Folding it at all,
 * rather than skipping it, is what freezes a raised card where it was
 * (A7, A11): the debounce stamps when it stopped being readable and voids
 * any clear dwell that was running.
 *
 * NOR IS ITS END. Such a spell -- or a car lost, or a write gate that
 * can't be read or is off -- ends with a new identity but, as often as
 * not, the same data. A key that can be read again from the data it was
 * last read from gets nothing new (`AttentionBook.read`, `stale`): one
 * row saying "kept out", held through a clamp, is still one reading once
 * the clamp lifts, so it never raises on its own, and a card it held
 * starts its clear dwell only with the next NEW reading that no longer
 * shows it. The site row is read by what it says, so a row that leaves
 * and comes back on silence alone is the same data.
 *
 * A reading's time is when this page received it, on the device's wall
 * clock (`Date.now()`), as the selector reads every other time on.
 */
import type { DoorViewModel, LiftViewModel } from '../../../api/adapters';
import type { RobotStateView, TaskStateView } from '../../../api/rmf-client';
import { robotReceiptKey } from '../../../api/robot-receipts';
import type { DoorRegistry, MaintenanceWindowFields } from '../../../api/v1';
import { audienceFor, type RoleLike } from '../../../auth/roles';
import type { RuntimeFeatureFlags } from '../../../config/runtime-config';
import { alertSubject, type AttentionAlert } from '../../../state/attention/alert-subject';
import {
  CLEARED_SHOWN_FOR_MS,
  EMPTY_DEBOUNCE,
  RAISE_AFTER_MS,
  advance,
  glance,
  type DebounceState,
  type DebouncedStatus,
  type Reading,
} from '../../../state/attention/debounce';
import { CANCEL_TAKES_EFFECT_MS, type EstopJob } from '../../../state/attention/estop';
import {
  ALERT_REACH_MS,
  PLEASE_CALL_AFTER_MS,
  SERVER_RULE_DWELLS,
  type AlertMuteLike,
} from '../../../state/attention/evidence';
import {
  HELD_CARD_AFTER_MS,
  WAIT_CALL_AFTER_MS,
  WAIT_SHOWN_AFTER_MS,
  siteRowCars,
  type LiftReadingInput,
} from '../../../state/attention/lift-items';
import type { RightNowRobot } from '../../../state/attention/right-now';
import {
  doorAlertRobot,
  liftCannotRead,
  liftConditionsShown,
  robotCannotRead,
  robotConditionsShown,
  stallDoor,
  type AttentionDoorInput,
  type AttentionInput,
  type AttentionRobotInput,
  type ConditionAbout,
  type DoorRobot,
  type MapPoint,
  type SourceHealth,
  type StallDoor,
} from '../../../state/attention/select';
import { msUntilNextMinute } from '../../../state/clock';
import { liftLandingLevel, sessionRobotIds } from '../../../state/lift-landing';
import { isSimulatedFleet } from '../../../state/simulated-fleet';
import type { LiftColumnState } from '../../Devices/lift-column-state';
import { lastReportAtMs } from '../../Fleet/robot-lines';
import type { PresenceKind, RobotPresence } from '../../Map/robot-presence';
import { heldLift, isTerminalTaskStatus } from '../../Tasks/task-ownership';

// ---------------------------------------------------------------------------
// Who gets the list
// ---------------------------------------------------------------------------

export type AttentionFlags = Pick<
  RuntimeFeatureFlags,
  'attentionEnabled' | 'attentionForSiteStaff'
>;

/**
 * Whether this viewer gets the attention list (contract §8): an ops
 * audience under `attentionEnabled`, a site audience -- `service_operator`
 * included (A2), and a login with no known role -- under
 * `attentionForSiteStaff`. Both default OFF: only `true` turns one on.
 */
export function attentionFlagOn(
  roles: Iterable<RoleLike> | null | undefined,
  flags: AttentionFlags | null | undefined,
): boolean {
  return audienceFor(roles) === 'ops'
    ? flags?.attentionEnabled === true
    : flags?.attentionForSiteStaff === true;
}

// ---------------------------------------------------------------------------
// Source health
// ---------------------------------------------------------------------------

/** One query's answer as a source health: failed over loading over ok. A
 *  query that failed a refetch keeps its last data, and is still failed. */
export function queryHealth(q: {
  readonly isError?: boolean;
  readonly isPending?: boolean;
}): SourceHealth {
  if (q.isError === true) return 'failed';
  if (q.isPending === true) return 'loading';
  return 'ok';
}

const HEALTH_RANK: Readonly<Record<SourceHealth, number>> = { ok: 0, loading: 1, failed: 2 };

/** A source read from several queries is as healthy as the worst of them. */
export function worstHealth(...healths: SourceHealth[]): SourceHealth {
  return healths.reduce<SourceHealth>((a, b) => (HEALTH_RANK[b] > HEALTH_RANK[a] ? b : a), 'ok');
}

// ---------------------------------------------------------------------------
// Robots
// ---------------------------------------------------------------------------

/**
 * W3's presence as the attention list reads it: which of a robot's
 * conditions can be read now (`robotCannotRead`). A rider or settling
 * robot seen only once (`presence.checking`, a REST seed or a replayed
 * row) is not vouched for, so its silence is no evidence either: it reads
 * as `checking`. Null: no presence (the robot is not in the tracker's
 * list), read as readable, as the selector says.
 */
export function attentionPresence(p: RobotPresence | undefined): PresenceKind | null {
  if (!p) return null;
  return p.checking ? 'checking' : p.kind;
}

/**
 * The floor a person can be sent to: the robot's own pose while presence
 * calls it live or quiet (never seen-once: W3 flags only checking,
 * riding and settling robots so). Not while checking (possibly a replay),
 * riding (its pose names the floor it boarded on), settling (the pose is
 * still updating after the ride), lost (the ghost is where it WAS) or
 * unknown.
 */
export function trustedLevel(p: RobotPresence | undefined): string | null {
  if (!p) return null;
  if (p.kind !== 'live' && p.kind !== 'quiet') return null;
  return p.pose?.source === 'live' ? p.pose.map : null;
}

/** Where on that floor (`trustedLevel`) the robot stands, in the building
 *  map's metres; null whenever its floor is not trusted. */
export function trustedPoint(p: RobotPresence | undefined): MapPoint | null {
  return p?.pose && trustedLevel(p) !== null ? mapPoint(p.pose.x, p.pose.y) : null;
}

/** A point on the building map, when both its numbers are finite. */
function mapPoint(x: unknown, y: unknown): MapPoint | null {
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)
    ? { x, y }
    : null;
}

export type AttentionTask = Pick<TaskStateView, 'id' | 'status' | 'assignedRobot'>;

/**
 * The robot's job, for the e-stop card.
 *
 *   - It carries a task: `ended` once that task reads ended in the task
 *     list, else `active` -- a task the list does not have (not loaded, or
 *     paged out) is active, so the card keeps "wait until it says Job
 *     cancelled" rather than offering the release. The cancel time is when
 *     this page saw a Cancel for it accepted.
 *   - It carries none. The robot drops the task id as a cancel lands, so
 *     a Cancel this page saw accepted for a task the list assigns to it is
 *     how its job can still be known. One that does not read ended yet is
 *     `active`, with that cancel's time: the card keeps "wait until it
 *     says Job cancelled", and the 30 s check runs -- the robot dropping
 *     the id is not the job ending, and a task that never ends (task 2585)
 *     is the hazard the card exists for. Else the latest that reads ended
 *     is `ended` ("Job cancelled"). Else `none`. A running one outranks
 *     any that ended: releasing into a live job is the hazard. The task
 *     list carries no fleet, so the robot is matched by name alone.
 *
 * `cancels`: task id -> device ms this page first saw a Cancel (or Kill)
 * for it accepted.
 */
export function estopJob(
  robot: Pick<RobotStateView, 'name' | 'taskId'>,
  tasksById: ReadonlyMap<string, AttentionTask>,
  cancels: ReadonlyMap<string, number>,
): EstopJob {
  const taskId = robot.taskId;
  if (taskId) {
    const task = tasksById.get(taskId);
    if (task && isTerminalTaskStatus(task.status)) return { state: 'ended', taskId };
    return { state: 'active', taskId, cancelRequestedAtMs: cancels.get(taskId) ?? null };
  }
  let running: { taskId: string; at: number } | null = null;
  let ended: { taskId: string; at: number } | null = null;
  for (const [id, at] of cancels) {
    const task = tasksById.get(id);
    if (!task || task.assignedRobot !== robot.name) continue;
    if (isTerminalTaskStatus(task.status)) {
      if (!ended || at > ended.at) ended = { taskId: id, at };
    } else if (!running || at > running.at) {
      running = { taskId: id, at };
    }
  }
  if (running) return { state: 'active', taskId: running.taskId, cancelRequestedAtMs: running.at };
  return ended ? { state: 'ended', taskId: ended.taskId } : { state: 'none' };
}

export interface AttentionRobotsData {
  readonly records: readonly RobotStateView[] | undefined;
  /** The registry's issue categories, by `<fleet>/<robot>`. */
  readonly issues: ReadonlyMap<string, { readonly issues: readonly string[] }> | undefined;
  /** W3's presence, by `<fleet>/<robot>`. */
  readonly presence: ReadonlyMap<string, RobotPresence>;
}

const NO_ISSUES: readonly string[] = Object.freeze([]);

/** Every robot record as the selector reads it. */
export function attentionRobotRows(
  robots: AttentionRobotsData,
  tasks: readonly AttentionTask[] | undefined,
  cancels: ReadonlyMap<string, number>,
  lifts: readonly LiftViewModel[] | undefined,
): AttentionRobotInput[] {
  const tasksById = new Map((tasks ?? []).map((t) => [t.id, t] as const));
  return (robots.records ?? []).map((robot) => {
    const key = robotReceiptKey(robot.fleet, robot.name);
    const p = robots.presence.get(key);
    return {
      fleet: robot.fleet,
      name: robot.name,
      status: robot.status,
      unavailableReason: robot.unavailableReason ?? null,
      issues: robots.issues?.get(key)?.issues ?? NO_ISSUES,
      level: trustedLevel(p),
      at: trustedPoint(p),
      simulated: isSimulatedFleet(robot.fleet),
      job: estopJob(robot, tasksById, cancels),
      heldLift: heldLift(robot, lifts)?.name ?? null,
      lastReportAtMs: lastReportAtMs(robot, p),
      presence: attentionPresence(p),
    };
  });
}

/** Every robot record with its presence, for "Right now". */
export function rightNowRobots(robots: AttentionRobotsData): RightNowRobot[] {
  return (robots.records ?? []).flatMap((robot) => {
    const presence = robots.presence.get(robotReceiptKey(robot.fleet, robot.name));
    if (!presence) return [];
    return [
      {
        fleet: robot.fleet,
        name: robot.name,
        status: robot.status,
        batteryPct: robot.battery ?? null,
        simulated: isSimulatedFleet(robot.fleet),
        presence,
      },
    ];
  });
}

// ---------------------------------------------------------------------------
// Lifts
// ---------------------------------------------------------------------------

/**
 * Every car of W6's column state as the selector reads it: W6's reading,
 * verbatim, plus the facts it does not carry (`LiftReadingInput`).
 *
 *   level           where a link to the car lands (`liftLandingLevel`)
 *   fault           the row's `fault`, null with no row or none reported
 *   session         the row's adapter session
 *   waitingRobots   the robots the session names (owner, RMF echo,
 *                   standing request) that are in the roster: an id the
 *                   console cannot find is somebody else's session
 */
export function attentionLiftReadings(
  columns: ReadonlyMap<string, LiftColumnState>,
  lifts: readonly LiftViewModel[] | undefined,
  robots: readonly RobotStateView[] | undefined,
  presence: ReadonlyMap<string, RobotPresence>,
): LiftReadingInput[] {
  const rows = new Map<string, LiftViewModel>();
  for (const row of lifts ?? []) if (!rows.has(row.name)) rows.set(row.name, row);
  const roster = new Map((robots ?? []).map((r) => [robotReceiptKey(r.fleet, r.name), r] as const));
  return [...columns.values()].map((column) => {
    const row = rows.get(column.name);
    const waitingRobots = row
      ? sessionRobotIds(row).flatMap((id) => {
          const r = roster.get(id);
          return r ? [{ fleet: r.fleet, name: r.name }] : [];
        })
      : [];
    return {
      ...column.reading,
      robotUse: column.facts.use,
      level: row ? liftLandingLevel(row, robots, presence).level : null,
      fault: row ? (row.fault ?? null) : null,
      session: row?.session ?? null,
      waitingRobots,
    };
  });
}

// ---------------------------------------------------------------------------
// Doors
// ---------------------------------------------------------------------------

/**
 * The door's last request (`DoorViewModel.lastCommand`, worker 0.4.21+)
 * when a ROBOT made it, OPEN or CLOSE (`AttentionDoorInput.robotRequest`).
 * RMF names the requester `<fleet>/<robot>`; split at the LAST slash, as
 * `alerts_engine._door_requesting_robot` splits it. Null for a console's
 * or anyone else's request, a requester with nothing after its slash, and
 * no record at all.
 */
export function doorRobotRequest(
  door: Pick<DoorViewModel, 'lastCommand'>,
): AttentionDoorInput['robotRequest'] {
  const c = door.lastCommand;
  if (!c || c.source !== 'robot') return null;
  const actor = c.actor.trim();
  const slash = actor.lastIndexOf('/');
  const robot = actor.slice(slash + 1);
  if (!robot) return null;
  return {
    fleet: slash > 0 ? actor.slice(0, slash) : null,
    robot,
    requested: c.requested,
    atMs: c.atMs,
  };
}

/**
 * Every door the console knows, as the alert-row cards read it: whether it
 * reads open, the robot behind its last request (`doorRobotRequest`), and
 * its floor and place. A live door's state carries neither
 * (`adaptV1DoorState`), so both are the building map's (`doors_registry`):
 * the floor as the Alerts table's Where column reads it
 * (`useAlertWhereFacts`), a row that carries one itself read first; the
 * place the registry's first end of the door, else the row's own (a row
 * only the registry knows carries it). Sorted by name, so the same doors
 * read the same way whatever order the list arrives in.
 */
export function attentionDoors(
  doors: readonly DoorViewModel[] | undefined,
  registry: readonly DoorRegistry[] | undefined,
): AttentionDoorInput[] {
  const levels = new Map<string, string>();
  const points = new Map<string, MapPoint>();
  for (const d of registry ?? []) {
    if (!d?.id?.door) continue;
    if (d.level) levels.set(d.id.door, d.level);
    const at = mapPoint(d.v1_x, d.v1_y);
    if (at) points.set(d.id.door, at);
  }
  const out = new Map<string, AttentionDoorInput>();
  for (const d of doors ?? []) {
    if (!d.name || out.has(d.name)) continue;
    out.set(d.name, {
      name: d.name,
      level: d.level || levels.get(d.name) || null,
      at: points.get(d.name) ?? (d.v1 ? mapPoint(d.v1.x, d.v1.y) : null),
      open: d.state === 'open',
      robotRequest: doorRobotRequest(d),
    });
  }
  return [...out.values()].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
}

/**
 * The robot each open door alert is about, kept from one reading to the
 * next (`doorAlertRobot`): an alert this page has named a robot for keeps
 * that robot while it stays open; one it has not is named from the door's
 * live state now, when that names one. An alert no longer open is
 * dropped, but only on a list that was read: a failed or loading read
 * closes nothing (the selector places nothing then either). The kept map
 * comes back as it was when nothing changed, so a reading that names no
 * one new is not a new input.
 */
export function rememberDoorAlertRobots(
  kept: ReadonlyMap<string, DoorRobot>,
  alerts: { readonly health: SourceHealth; readonly rows: readonly AttentionAlert[] },
  doors: readonly AttentionDoorInput[],
): ReadonlyMap<string, DoorRobot> {
  if (alerts.health !== 'ok') return kept;
  const next = new Map<string, DoorRobot>();
  for (const alert of alerts.rows) {
    const subject = alertSubject(alert);
    if (subject?.kind !== 'door_open_failed' && subject?.kind !== 'door_passage_stalled') continue;
    const robot = doorAlertRobot(alert.id, doors.find((d) => d.name === subject.door), kept);
    if (robot) next.set(alert.id, robot);
  }
  // A kept name is never replaced, so the map is new only when an alert
  // was named or dropped.
  const same = next.size === kept.size && [...next.keys()].every((id) => kept.has(id));
  return same ? kept : next;
}

/**
 * The door each open `robot_stalled` alert is stuck at, kept from one
 * reading to the next (`stallDoor`): an alert this page has found at a
 * door keeps that door while it stays open, and once the door has opened
 * for its robot it stays opened; one it has not -- nor could place
 * (`unplaced`) -- is read from the doors' live state now (`robots` give
 * the robot's floor and point). Dropped and kept as
 * `rememberDoorAlertRobots` drops and keeps them. The kept map comes back
 * as it was when nothing changed: `stallDoor` hands back the kept entry
 * itself until it opens.
 */
export function rememberStallDoors(
  kept: ReadonlyMap<string, StallDoor>,
  alerts: { readonly health: SourceHealth; readonly rows: readonly AttentionAlert[] },
  doors: readonly AttentionDoorInput[],
  robots: readonly Pick<AttentionRobotInput, 'name' | 'level' | 'at'>[],
): ReadonlyMap<string, StallDoor> {
  if (alerts.health !== 'ok') return kept;
  const next = new Map<string, StallDoor>();
  for (const alert of alerts.rows) {
    const at = stallDoor(alert, robots, doors, kept);
    if (at !== null && at !== 'unplaced') next.set(alert.id, at);
  }
  const same = next.size === kept.size && [...next].every(([id, at]) => kept.get(id) === at);
  return same ? kept : next;
}

// ---------------------------------------------------------------------------
// Planned maintenance
// ---------------------------------------------------------------------------

/**
 * When the planned maintenance that covers `nowMs` ends, or null when none
 * does (or the windows could not be read). Half-open `[start, end)`, as the
 * server tests it; a window that starts exactly as another ends continues
 * it, so back-to-back windows read as one.
 */
export function maintenanceUntilMs(
  windows: readonly MaintenanceWindowFields[] | null | undefined,
  nowMs: number,
): number | null {
  if (!windows) return null;
  let until: number | null = null;
  let at = nowMs;
  for (;;) {
    let next: MaintenanceWindowFields | null = null;
    for (const w of windows) {
      if (w.starts_at_ms <= at && at < w.ends_at_ms && (!next || w.ends_at_ms > next.ends_at_ms))
        next = w;
    }
    if (!next) return until;
    until = next.ends_at_ms;
    at = next.ends_at_ms;
  }
}

// ---------------------------------------------------------------------------
// The debounce, owned
// ---------------------------------------------------------------------------

/** A trusted reading of a car, live or quiet alike: quiet is only the clock
 *  passing since its last row. */
const settled = <S extends string>(state: S): S | 'live' => (state === 'quiet' ? 'live' : state);

/**
 * What a reading of one car reads of W6's column state, as a key: a NEW key
 * is a new reading of that car, the same key is not (A17a).
 *
 * W6 hands back a new column whenever what the car shows moves, its
 * freshness boundaries included. A car going quiet moves its look (solid
 * to dim), its door and motion (withdrawn) and its reading's `freshness`,
 * but says nothing new: the row is the one already read, and `liftReading`
 * reads a live and a quiet car alike. Folded as a reading, that flip was a
 * second reading of one row, and a car that said "kept out" once raised
 * its card 10 s later. So quiet reads as live here, and the look, door,
 * motion and liveness -- which follow from freshness or come with a new
 * row anyway -- are left out. Everything else the car says is in: a flip
 * to lost (or checking, or off) changes the key, so the car that can't be
 * read is read as such and holds its card (A7).
 */
export function liftCarReadingKey(c: LiftColumnState): string {
  return JSON.stringify({
    name: c.name,
    state: settled(c.freshness.state),
    lostBecause: c.freshness.lostBecause,
    caveat: c.caveat,
    level: c.level,
    facts: c.facts,
    reading: { ...c.reading, freshness: settled(c.reading.freshness) },
  });
}

/** Every car's `liftCarReadingKey`, together: what a look at the cars'
 *  silence reads of the column state. */
export function liftColumnsReadingKey(columns: ReadonlyMap<string, LiftColumnState>): string {
  return [...columns.values()].map(liftCarReadingKey).join('\n');
}

/** One car's own source: what that car said (A19). */
export type LiftCarSource = `lift-car:${string}`;

/** W6's site row's own source: what it says, but its silence (A19). */
export const LIFT_SITE_SOURCE = 'lift-site';

/** The source of what `car` said. */
export function liftCarSource(car: string): LiftCarSource {
  return `lift-car:${car}`;
}

/** The debounce's sources: the robots; each car (`liftCarSource`); W6's
 *  site row (`LIFT_SITE_SOURCE`); and `lift-silence`, the lift keys whose
 *  condition is the silence itself (`isLiftSilenceKey`), read on the clock
 *  as well as on new data. */
export type AttentionSource = 'robots' | 'lift-silence' | typeof LIFT_SITE_SOURCE | LiftCarSource;

/**
 * The lift keys whose condition IS a car's silence: a robot car with no
 * reading (`lift:<car>:no_reading`), and the site row W6 gives while a car
 * between floors has no trustworthy reading and none reads usable ("Can't
 * tell", `site:lift:cant_tell`). W6 decides both on the device clock, so
 * a later look at the same column state is a new reading of them.
 */
export function isLiftSilenceKey(key: string): boolean {
  return key === 'site:lift:cant_tell' || (key.startsWith('lift:') && key.endsWith(':no_reading'));
}

/** A reading of the robots, for `advance`: what it shows, and what it
 *  cannot speak for (A7). Unreadable -- failed, loading, or through a
 *  clamped feed -- it shows nothing and speaks for nothing. */
export function robotsReading(
  robots: AttentionInput['robots'],
  roles: readonly RoleLike[],
  feed: AttentionInput['feed'],
  atMs: number,
): Reading<ConditionAbout> {
  const readable = robots.health === 'ok' && !feed.clamped;
  return {
    source: 'robots',
    atMs,
    active: readable ? robotConditionsShown(robots.rows, roles) : new Map(),
    cannotRead: robotCannotRead(robots, feed),
  };
}

function liftsPart(
  source: AttentionSource,
  shows: (key: string) => boolean,
  lifts: AttentionInput['lifts'],
  feed: AttentionInput['feed'],
  atMs: number,
): Reading<ConditionAbout> {
  const readable = lifts.health === 'ok' && !feed.clamped;
  const active = new Map<string, ConditionAbout>();
  if (readable) {
    for (const [key, about] of liftConditionsShown(lifts.readings, lifts.siteRow)) {
      if (shows(key)) active.set(key, about);
    }
  }
  // Each source's keys are its own, so `cannotRead` is asked only of them.
  return { source, atMs, active, cannotRead: liftCannotRead(lifts, feed) };
}

/** A reading of what one car SAID (A19), as `robotsReading`: its keys but
 *  its silence's. A car the readings no longer list shows nothing, and is
 *  not unread (`liftCannotRead`): it is not a car any more. */
export function liftCarReading(
  car: string,
  lifts: AttentionInput['lifts'],
  feed: AttentionInput['feed'],
  atMs: number,
): Reading<ConditionAbout> {
  const prefix = `lift:${car}:`;
  return liftsPart(
    liftCarSource(car),
    (key) => key.startsWith(prefix) && !isLiftSilenceKey(key),
    lifts,
    feed,
    atMs,
  );
}

/** A reading of what W6's site row says (A19), as `robotsReading`: its
 *  keys but its silence's ("Can't tell"). */
export function liftSiteReading(
  lifts: AttentionInput['lifts'],
  feed: AttentionInput['feed'],
  atMs: number,
): Reading<ConditionAbout> {
  return liftsPart(
    LIFT_SITE_SOURCE,
    (key) => key.startsWith('site:lift:') && !isLiftSilenceKey(key),
    lifts,
    feed,
    atMs,
  );
}

/** A reading of the cars' silence (`isLiftSilenceKey`), as
 *  `robotsReading`. */
export function liftSilenceReading(
  lifts: AttentionInput['lifts'],
  feed: AttentionInput['feed'],
  atMs: number,
): Reading<ConditionAbout> {
  return liftsPart('lift-silence', isLiftSilenceKey, lifts, feed, atMs);
}

/**
 * The cars W6's site row speaks for (`siteRowCars`): a new row from any of
 * them is a new reading of the site row; a row from any other car is not
 * (A19). None for no row.
 */
export function liftSiteRowCarNames(
  siteRow: AttentionInput['lifts']['siteRow'],
  readings: AttentionInput['lifts']['readings'],
): string[] {
  return siteRow ? siteRowCars(siteRow, readings).map((r) => r.name) : [];
}

/** What a source can be read for now (`AttentionBook.read`): a key that
 *  changes exactly when the answer does, and the answer, asked only when
 *  a glance is folded. */
export interface SourceUnread {
  readonly key: string;
  readonly cannotRead: () => (key: string) => boolean;
}

function sameIdentity(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
}

/**
 * The debounce state of one page's attention list, advanced once per
 * reading. One book per site and audience: a key names a car or a robot
 * of one site, and which conditions a reading shows depends on who reads.
 */
export class AttentionBook {
  private _state: DebounceState<ConditionAbout> = EMPTY_DEBOUNCE;
  private readonly _seen = new Map<AttentionSource, readonly unknown[]>();
  private readonly _unread = new Map<AttentionSource, string>();
  /** Per key with an entry: the identity of the last reading that could
   *  speak for it (`read`, a spell that ends on the same data). */
  private readonly _keyRead = new Map<string, readonly unknown[]>();

  get state(): DebounceState<ConditionAbout> {
    return this._state;
  }

  /**
   * Fold one reading of `source`, read from `identity` (the data, by
   * reference). The same identity again folds nothing: a re-render is not
   * a second reading -- unless `onTheClock`, for a source the device clock
   * decides (`lift-silence`): then the same identity folds again once the
   * wall clock reaches `dueAtMs`, as a later look at the silence. A
   * reading is timed at `wallNowMs`, or 1 ms after this source's last one
   * when the clock has not moved, so two readings are never one. Returns
   * whether it folded.
   *
   * `unread`: what this source can be read for now, when that moves on
   * the clock (the robots' presence). The same identity with a different
   * `unread.key` from the last call's folds a GLANCE (`glance`): it dates
   * the hold of what can no longer be read at this instant, and reads
   * nothing, so it raises, clears and counts toward nothing (A17b). A new
   * identity is a reading, which reads `cannotRead` itself.
   *
   * A SPELL THAT ENDS ON THE SAME DATA. A source that could not be read
   * for a while -- the feed clamped, a query failed, the write gate
   * unreadable or off, the car lost -- comes back with a new identity
   * (health, the clamp and W6's column state are in it) but, often, the
   * same data: a reconnect replay of unchanged rows keeps their
   * references. A key it can speak for again whose last READABLE reading
   * was of this same identity gets nothing new from it (`stale`): it is
   * folded as a glance would fold it. So one row saying "kept out", held
   * through the spell, is still one reading when the spell ends. Data that
   * first arrived during the spell is new: it is read once it can be.
   * Not for a source on the clock: a later look at the silence is a
   * reading by design.
   */
  read(
    source: AttentionSource,
    identity: readonly unknown[],
    reading: (atMs: number) => Reading<ConditionAbout>,
    wallNowMs: number,
    onTheClock = false,
    unread?: SourceUnread,
  ): boolean {
    const prev = this._seen.get(source);
    const lastUnread = this._unread.get(source);
    if (unread) this._unread.set(source, unread.key);
    const atMs = this.nextAtMs(source, wallNowMs);
    if (prev && sameIdentity(prev, identity)) {
      const due = onTheClock ? this.dueAtMs(source) : null;
      if (due === null || wallNowMs < due) {
        // No new data, but what can be read of it moved on the clock: a
        // glance, which dates the hold and reads nothing (A17b).
        if (!unread || lastUnread === undefined || lastUnread === unread.key) return false;
        this._state = glance(this._state, { source, atMs, cannotRead: unread.cannotRead() });
        return true;
      }
    }
    this._seen.set(source, identity);
    const r = reading(atMs);
    const stale = onTheClock ? null : this.staleKeys(source, identity, r);
    this._state = advance(this._state, stale ? { ...r, stale: (key) => stale.has(key) } : r);
    // A key with no entry has no run to protect.
    for (const key of this._keyRead.keys())
      if (!this._state.entries.has(key)) this._keyRead.delete(key);
    return true;
  }

  /**
   * The keys of `source` that `r` can speak for and whose last readable
   * reading was of this same `identity` (see `read`); the others it can
   * speak for are noted as read from it. Null for none.
   */
  private staleKeys(
    source: AttentionSource,
    identity: readonly unknown[],
    r: Reading<ConditionAbout>,
  ): Set<string> | null {
    const keys = new Set<string>(r.active.keys());
    for (const [key, e] of this._state.entries) if (e.source === source) keys.add(key);
    let stale: Set<string> | null = null;
    for (const key of keys) {
      if (r.cannotRead?.(key) === true) continue;
      const last = this._keyRead.get(key);
      if (last !== undefined && sameIdentity(last, identity)) (stale ??= new Set()).add(key);
      else this._keyRead.set(key, identity);
    }
    return stale;
  }

  /** A fold of `source` at `wallNowMs`, or 1 ms after its last one when
   *  the clock has not moved, so two folds are never one. */
  private nextAtMs(source: AttentionSource, wallNowMs: number): number {
    const last = this._state.lastReadingAt.get(source);
    return last === undefined ? wallNowMs : Math.max(wallNowMs, last + 1);
  }

  /**
   * The soonest instant a key of `source` that a reading has shown, and
   * that has not raised, could raise -- its run's first reading plus
   * `RAISE_AFTER_MS` -- and that no reading has looked at yet; null when
   * none is waiting. Once a reading at or after it has been folded it is
   * not due again, so an unreadable look (which leaves the dwell as it
   * was) is not repeated on every render.
   */
  dueAtMs(source: AttentionSource): number | null {
    const last = this._state.lastReadingAt.get(source) ?? Number.NEGATIVE_INFINITY;
    let due: number | null = null;
    for (const e of this._state.entries.values()) {
      if (e.source !== source || e.raised || e.firstTrueAt === null) continue;
      const at = e.firstTrueAt + RAISE_AFTER_MS;
      if (at > last && (due === null || at < due)) due = at;
    }
    return due;
  }

  /**
   * The cars whose own source still holds a key (A19), whether or not the
   * column state still lists them: a car gone from it (the asset failed to
   * load, or a re-export dropped the car) is still read, so a failed source
   * holds its card (A7) and a car the building no longer has clears.
   */
  heldLiftCars(): string[] {
    const cars = new Set<string>();
    for (const e of this._state.entries.values()) {
      if (e.source.startsWith('lift-car:')) cars.add(e.source.slice('lift-car:'.length));
    }
    return [...cars];
  }

  /** The newest reading of any source; null before the first. */
  lastReadingAtMs(): number | null {
    let latest: number | null = null;
    for (const at of this._state.lastReadingAt.values())
      if (latest === null || at > latest) latest = at;
    return latest;
  }
}

// ---------------------------------------------------------------------------
// When the list would change with no new data
// ---------------------------------------------------------------------------

export interface AttentionClockInput {
  readonly nowMs: number;
  readonly view: ReadonlyMap<string, DebouncedStatus<ConditionAbout>>;
  readonly robots: readonly Pick<AttentionRobotInput, 'job'>[];
  readonly alerts: readonly Pick<AttentionAlert, 'openedAtMs'>[];
  readonly mutes: readonly AlertMuteLike[] | null;
  readonly maintenance: readonly MaintenanceWindowFields[] | null;
  /** When a source read on the clock is due a reading with no new data
   *  (`AttentionBook.dueAtMs`); null for none. */
  readonly readingDueAtMs?: number | null;
}

/**
 * The first instant after `nowMs` at which the list could read differently
 * with no new data: the next wall-clock minute (durations, "since"), a
 * "Cleared at" going after its 2 minutes, a lift hold or hall wait
 * reaching its card (5 min, 3 min, 10 min), the server's alert falling due
 * for a condition seen (`SERVER_RULE_DWELLS` plus the poll allowance), an
 * unacknowledged alert asking for a call (5 min), a Cancel that has not
 * taken effect (30 s), a mute expiring, a maintenance window starting or
 * ending, a car's silence due its second reading. A superset is harmless
 * -- one more render with nothing changed -- and the minute keeps it
 * finite.
 */
export function nextAttentionChangeAtMs(input: AttentionClockInput): number {
  const { nowMs } = input;
  const at: number[] = [nowMs + msUntilNextMinute(nowMs)];
  for (const v of input.view.values()) {
    if (v.status === 'cleared') {
      at.push(v.clearedAtMs + CLEARED_SHOWN_FOR_MS);
      continue;
    }
    for (const after of [HELD_CARD_AFTER_MS, WAIT_SHOWN_AFTER_MS, WAIT_CALL_AFTER_MS])
      at.push(v.seenSinceMs + after);
    for (const { dwellMs } of Object.values(SERVER_RULE_DWELLS))
      at.push(v.seenSinceMs + dwellMs + ALERT_REACH_MS);
  }
  for (const r of input.robots) {
    if (r.job.state === 'active' && r.job.cancelRequestedAtMs !== null) {
      at.push(r.job.cancelRequestedAtMs + CANCEL_TAKES_EFFECT_MS);
    }
  }
  for (const a of input.alerts)
    if (typeof a.openedAtMs === 'number') at.push(a.openedAtMs + PLEASE_CALL_AFTER_MS);
  for (const m of input.mutes ?? []) if (m.expires_at_ms !== null) at.push(m.expires_at_ms);
  for (const w of input.maintenance ?? []) at.push(w.starts_at_ms, w.ends_at_ms);
  if (typeof input.readingDueAtMs === 'number') at.push(input.readingDueAtMs);
  return Math.min(...at.filter((t) => Number.isFinite(t) && t > nowMs));
}
